import isOnline from 'is-online';
import { Client } from 'weathered';
import { categoryDayFetchUrl, countyFireZoneToNameMap, countyFireZoneToPILMap, NWSApiUrl } from '../config/nws.constants';
import { getJson, saveJson } from './offline/cache.service';
import { _requestJson, _requestText } from './request.service';

const client = new Client();

/**
 * Fetches weather information and category day from servers and cache data locally.
 * @param {number} lat 
 * @param {number} lng 
 */
export const fetchAndCacheWeatherInfo = async (lat, lng) => {
	const [info, catDay] = await Promise.all([
		getLatestWeatherObservationsForLatLng(lat, lng),
		getCategoryDayForLatLng(lat, lng)
	]);

	const {windSpeed, windDirection, relativeHumidity} = info;

	saveJson('weather', {
		weatherInfo: {windSpeed, windDirection, relativeHumidity},
		catDay
	});
}

/**
 * @returns {{weatherInfo: WeatherInfo, catDay: number} | null} weather info data cached locally
 * 
 */
export const getCachedWeatherInfo = () => {
	return getJson('weather');
}

/**
 * Use NWS API to get various weather information from the weather station closest to lat,lng.
 * If offline, returns cached data (for user's last saved location) if present.
 * @param {number} lat 
 * @param {number} lng 
 * @returns {}
 */
export async function getLatestWeatherObservationsForLatLng(lat, lng) {
	if (!(await isOnline())) {
		const cached = getCachedWeatherInfo();
		return cached ? cached.weatherInfo : undefined;
	}

	// const st = await client.getStations(lat, lng)
	const nearestStation = await client.getNearestStation(lat, lng);
	if (nearestStation) {
		const { stationIdentifier } = nearestStation.properties;
		try {
			const observations = await client.getLatestStationObservations(stationIdentifier);
			return observations.properties;
		} catch (e) {
			console.log("Error fetching weather observations: ", e);
			return null;
		}
	} else {
		return null;
	}
}

/**
 * This function fetches textual information from the Iowa Environmental Mesonet service by Iowa State University: https://mesonet.agron.iastate.edu/wx/afos/#FWFSHV-1.
 * It then searches for the Category Day for the desired county in the returned text and returns it as a number.
 * 
 * The code was mostly copied from https://www.weather.gov/source/lzk/catday.js?v=1653573814867 with some modifications,
 * which is part of the source code for https://www.weather.gov/lzk/forest2.htm and controls the county dropdown in the "Category Day Information" section.
 * 
 * Ideally the Category Day would be fetched directly from the NWS API, but no endpoint was found for it.
 * @returns {number} representing the category day. If Offline returns cached category day if present.
 */
export async function getCategoryDayForLatLng(lat, lng) {
	if (!(await isOnline())) {
		const cached = getCachedWeatherInfo();
		return cached ? cached.catDay : undefined;
	}

	const fireZoneId = await getCountyFireWeatherZoneId(lat, lng);
	if (!fireZoneId) return null;

	const countyPIL = countyFireZoneToPILMap[fireZoneId];
	const countyZoneName = countyFireZoneToNameMap[fireZoneId];
	if (!countyPIL || !countyZoneName) return null;

	try {
		const dataText = await _requestText(`${categoryDayFetchUrl}${countyPIL}`, null);

		let myCatDayOne;
		let myUprText = dataText.toUpperCase();
		let findZone = myUprText.indexOf(countyZoneName);
		let myPostText = myUprText.substr(findZone);
		let endText = myPostText.indexOf("$$", 1);
		let myFireText = myPostText.substr(0, endText);
		let myPattern = myPostText.match(/[C][SD][T]/)
		let findTime = myFireText.indexOf(myPattern);
		let newLine = myFireText.substr(findTime);
		let prodAM = 0;
		let findTonight = newLine.indexOf("TONIGHT");
		if (findTonight > 50) {
			prodAM = 1;
		}
	
		let findCatDay = newLine.indexOf("CATEGORY DAY");
		if (findCatDay >= 0) {
			if (prodAM == 1) {
				myCatDayOne = newLine.substr(findCatDay + 22, 1).trim();
			} else {
				myCatDayOne = newLine.substr(findCatDay + 35, 1).trim();
			}
		}
		if (findCatDay < 0) {
			myCatDayOne = "";
		}
		return myCatDayOne;

	} catch (e) {
		console.log("Error fetching category day: ", e);
		return null;
	}
}

export function isBadWeather(windSpeed, humidity) {
	return windSpeed > 15 || humidity <= 20;
}

export function getCardinalDirectionFromAngle(angle) {
	if (angle === undefined || angle === null) return '';

	var directions = [
		'N',
		'NE',
		'E',
		'SE',
		'S',
		'SW',
		'W',
		'NW'
	];

	// const directions = [
	// 	'E',
	// 	'NE',
	// 	'N',
	// 	'NW',
	// 	'W',
	// 	'SW',
	// 	'S',
	// 	'SE',
	// ]
	var index = Math.round(((angle %= 360) < 0 ? angle + 360 : angle) / 45) % 8;
	return directions[index];
}

async function getCountyFireWeatherZoneId(lat, lng) {
	const url = `${NWSApiUrl}zones?type=fire&point=${lat},${lng}&include_geometry=false&limit=1`;
	const data = await _requestJson(url, null);
	if (!data || !data.features || !data.features.length || !data.features[0].properties) return null;
	return data.features[0].properties.id;
}

